import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
  catchError,
  map,
  merge,
  of,
  retry,
  switchMap,
  takeUntil,
  tap,
  throwError,
  timer,
} from "rxjs";
import { AuthService } from "./auth.service";
import { ConnectionStatus, NetworkService } from "./network.service";
import { OfflineQueueApiActions } from "../state";

@Injectable({
  providedIn: "root",
})
export class OfflineQueueService {
  private store = inject(Store);
  private authService = inject(AuthService);
  private networkService = inject(NetworkService);

  constructor() {
    this.onNetworkChange();
  }

  private onNetworkChange(): void {
    const networkStatus$ = this.networkService.onNetworkChange();
    const loginWhileOnline$ = this.authService.onLoginSuccess().pipe(
      map(() => this.networkService.getStatus())
    );

    merge(networkStatus$, loginWhileOnline$)
      .pipe(
        switchMap((status) => {
          if (
            status !== ConnectionStatus.Online ||
            !this.authService.isLoggedIn()
          ) {
            return of(status);
          }

          return this.authService.ping().pipe(
            retry({
              count: 5,
              delay: (error) =>
                error?.status >= 500 ||
                error?.error?.error === "server_side_error"
                  ? timer(10000)
                  : throwError(() => error),
            }),
            // Must follow retry so logout also cancels a pending retry timer.
            takeUntil(this.authService.onLogout()),
            tap(() => {
              this.store.dispatch(OfflineQueueApiActions.processOfflineQueue());
            }),
            catchError((err) => {
              console.error("Ping failed after retries", err);
              return of(status);
            })
          );
        })
      )
      .subscribe();
  }
}
